import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import type { CategoryItems } from '@/bindings/CategoryItems'
import type { Language } from '@/bindings/Language'
import type { Statistics } from '@/bindings/Statistics'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { Field, Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { statisticsQuery } from '@/features/back-office/api'
import { MonthPicker } from '@/features/back-office/month-picker'
import { useDraft } from '@/features/back-office/use-draft'
import {
  STATISTICS_TABS,
  type Span,
  type StatisticsSearch,
  type StatisticsTab,
  isIsoDate,
  pointOf,
  spanOf,
} from '@/features/back-office/statistics-params'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { formatPrice, formatQuantity } from '@/lib/menu-format'
import { canManageBranch } from '@/lib/roles'
import { formatDate, todayIn } from '@/lib/time'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/statistics'
export const STATISTICS_PATH = '/$restaurantSlug/$branchSlug/manage/statistics'
export const BESTSELLERS_PATH = '/$restaurantSlug/$branchSlug/manage/statistics/bestsellers'
export const ORDERS_OF_DATE_PATH = '/$restaurantSlug/$branchSlug/manage/statistics/orders'

interface Scope {
  restaurantSlug: string
  branchSlug: string
  restaurantId: string
  branchId: string
  /** The restaurant's main language: the T4.3 fallback for item and category names. */
  main: Language
  /** Today in the branch's zone (T14): every tab's default. */
  today: string
  /** The period lives in the URL, so a list page's back link lands on the same view. */
  go: (next: StatisticsSearch) => void
}

/**
 * Statistics (PRD, H5): the branch's sales over a day, a month, a year or a range of working
 * dates — bestsellers, orders by status, categories, order type. Admin and above in the branch.
 */
export function StatisticsPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const search = useSearch({ from: ROUTE_ID })
  const navigate = useNavigate()

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('stats.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
      {branch?.deactivated_at && <Badge tone="muted">{t('restaurants.deactivated')}</Badge>}
    </div>
  )
  if (!canManageBranch(context.role)) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p role="alert" className="text-muted text-sm">
          {t('stats.adminOnly')}
        </p>
      </Page>
    )
  }

  const scope: Scope = {
    restaurantSlug,
    branchSlug,
    restaurantId,
    branchId,
    main: context.settings.main_language,
    today: todayIn(branch?.timezone),
    go: (next) => void navigate({ to: STATISTICS_PATH, params: { restaurantSlug, branchSlug }, search: next }),
  }
  const span = spanOf(search, scope.today)

  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      <div role="tablist" aria-label={t('stats.tabs')} className="flex flex-wrap gap-2">
        {STATISTICS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={search.tab === tab}
            onClick={() => scope.go({ tab })}
            className={cx(
              'min-h-9 rounded-full border px-3 text-sm font-medium select-none',
              search.tab === tab ? 'bg-accent text-accent-fg border-accent' : 'border-border text-muted hover:bg-accent-soft',
            )}
          >
            {t(`stats.tab.${tab}`)}
          </button>
        ))}
      </div>
      <div role="tabpanel" className="flex flex-col gap-4">
        <Picker key={search.tab} scope={scope} search={search} />
        <Overview scope={scope} tab={search.tab} span={span} />
      </div>
    </Page>
  )
}

// -- period pickers: each change with a whole value goes straight to the URL --

function Picker({ scope, search }: { scope: Scope; search: StatisticsSearch }) {
  const point = pointOf(search, scope.today)
  switch (search.tab) {
    case 'date':
      return <DatePicker scope={scope} date={point.date} />
    case 'month':
      return <MonthPicker month={point.month} onChange={(month) => scope.go({ tab: 'month', month })} />
    case 'year':
      return <YearPicker scope={scope} year={point.year} />
    case 'range':
      return <RangePicker scope={scope} range={point.range} />
  }
}

function DatePicker({ scope, date }: { scope: Scope; date: string }) {
  const t = useT()
  return (
    <Field label={t('stats.date')} className="w-44">
      <Input type="date" value={date} onChange={(event) => isIsoDate(event.target.value) && scope.go({ tab: 'date', date: event.target.value })} />
    </Field>
  )
}

function YearPicker({ scope, year: initial }: { scope: Scope; year: string }) {
  const t = useT()
  const [year, setYear] = useDraft(initial)
  return (
    <Field label={t('stats.year')} className="w-28">
      <Input
        type="number"
        inputMode="numeric"
        min={2000}
        max={2100}
        step={1}
        value={year}
        onChange={(event) => {
          setYear(event.target.value)
          if (/^\d{4}$/.test(event.target.value)) scope.go({ tab: 'year', year: event.target.value })
        }}
      />
    </Field>
  )
}

function RangePicker({ scope, range }: { scope: Scope; range: Span }) {
  const t = useT()
  const [from, setFrom] = useDraft(range.from)
  const [to, setTo] = useDraft(range.to)
  const go = (from: string, to: string) => isIsoDate(from) && isIsoDate(to) && scope.go({ tab: 'range', from, to })
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={t('stats.from')} className="w-44">
        <Input
          type="date"
          value={from}
          max={to}
          onChange={(event) => {
            setFrom(event.target.value)
            go(event.target.value, to)
          }}
        />
      </Field>
      <Field label={t('stats.to')} className="w-44">
        <Input
          type="date"
          value={to}
          min={from}
          onChange={(event) => {
            setTo(event.target.value)
            go(from, event.target.value)
          }}
        />
      </Field>
    </div>
  )
}

// -- the overview --

/** "Sep 6, 2026", or "Sep 1, 2026 – Sep 6, 2026" for a span of days. */
export function SpanLabel({ span }: { span: Span }) {
  const t = useT()
  const language = useLanguage()
  const from = formatDate(span.from, language)
  return <p className="text-muted text-sm">{span.from === span.to ? from : t('stats.span', { from, to: formatDate(span.to, language) })}</p>
}

export function Loadable({ query, children }: { query: { isPending: boolean; data: unknown; error: unknown; refetch: () => unknown }; children: ReactNode }) {
  const t = useT()
  if (query.isPending) return <p className="text-muted text-sm">{t('common.loading')}</p>
  if (!query.data) {
    return (
      <div className="flex flex-col gap-2">
        <FormError error={query.error} />
        <Button variant="secondary" onClick={() => void query.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </div>
    )
  }
  return children
}

function Overview({ scope, tab, span }: { scope: Scope; tab: StatisticsTab; span: Span }) {
  const query = useQuery(statisticsQuery(scope.restaurantId, scope.branchId, span))
  return (
    <>
      <SpanLabel span={span} />
      <Loadable query={query}>{query.data && <Cards scope={scope} tab={tab} stats={query.data} />}</Loadable>
    </>
  )
}

function Cards({ scope, tab, stats }: { scope: Scope; tab: StatisticsTab; stats: Statistics }) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(scope.main)
  const decimals = minorUnits(stats.currency)
  const price = (value: string) => formatPrice(value, decimals, language)
  const params = { restaurantSlug: scope.restaurantSlug, branchSlug: scope.branchSlug }
  const span = { from: stats.from, to: stats.to }
  const section = (key: string) => ({ 'aria-labelledby': `stats-${key}` })
  const heading = (key: string, label: string) => (
    <h2 id={`stats-${key}`} className="font-semibold">
      {label}
    </h2>
  )

  return (
    <>
      <Card className="flex flex-col gap-1" {...section('sales')}>
        {heading('sales', t('stats.sales'))}
        <p className="text-2xl font-semibold tabular-nums">{price(stats.sales.total)}</p>
        <p className="text-muted text-sm">
          {t('stats.orders', { count: stats.sales.count })} · {t('stats.salesHint')}
        </p>
      </Card>

      <Card className="flex flex-col gap-3" {...section('bestsellers')}>
        <div className="flex items-center justify-between gap-2">
          {heading('bestsellers', t('stats.bestsellers'))}
          {stats.bestsellers.length > 0 && (
            <Link to={BESTSELLERS_PATH} params={params} search={{ ...span, tab }} className="text-accent text-sm font-medium hover:underline">
              {t('stats.allItems')}
            </Link>
          )}
        </div>
        <ItemList categories={stats.bestsellers} main={scope.main} decimals={decimals} />
      </Card>

      <Card className="flex flex-col gap-3" {...section('status')}>
        <div className="flex items-center justify-between gap-2">
          {heading('status', t('stats.byStatus'))}
          {tab === 'date' && (
            <Link to={ORDERS_OF_DATE_PATH} params={params} search={{ date: stats.from, page: 1 }} className="text-accent text-sm font-medium hover:underline">
              {t('stats.ordersOfDate')}
            </Link>
          )}
        </div>
        <Table
          label={t('stats.byStatus')}
          rows={stats.by_status.map((row) => ({ key: row.status, label: t(`track.status.${row.status}`), count: row.count, total: price(row.total) }))}
        />
      </Card>

      <Card className="flex flex-col gap-3" {...section('category')}>
        {heading('category', t('stats.byCategory'))}
        {stats.by_category.length === 0 ? (
          <p className="text-muted text-sm">{t('stats.nothingSold')}</p>
        ) : (
          <Table
            label={t('stats.byCategory')}
            quantity
            rows={stats.by_category.map((row) => ({
              key: row.category_id,
              label: name(row.name),
              count: row.order_count,
              quantity: formatQuantity(row.quantity, language),
              total: price(row.total),
            }))}
          />
        )}
      </Card>

      <Card className="flex flex-col gap-3" {...section('type')}>
        {heading('type', t('stats.byType'))}
        <Table
          label={t('stats.byType')}
          rows={stats.by_type.map((row) => ({ key: row.order_type, label: t(`stats.type.${row.order_type}`), count: row.count, total: price(row.total) }))}
        />
      </Card>
    </>
  )
}

interface Row {
  key: string
  label: string
  count: number
  quantity?: string
  total: string
}

/** Label · orders (· quantity) · total; numbers right-aligned and tabular. */
function Table({ label, rows, quantity = false }: { label: string; rows: Row[]; quantity?: boolean }) {
  const t = useT()
  return (
    <div className="overflow-x-auto">
      <table aria-label={label} className="w-full text-sm">
        <thead className="text-muted text-xs">
          <tr>
            <th scope="col" className="py-1 text-left font-medium" />
            <th scope="col" className="py-1 text-right font-medium">
              {t('stats.count')}
            </th>
            {quantity && (
              <th scope="col" className="py-1 text-right font-medium">
                {t('stats.quantity')}
              </th>
            )}
            <th scope="col" className="py-1 text-right font-medium">
              {t('stats.total')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-border divide-y">
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row" className="py-1.5 pr-2 text-left font-normal">
                {row.label}
              </th>
              <td className="py-1.5 pl-2 text-right tabular-nums">{row.count}</td>
              {quantity && <td className="py-1.5 pl-2 text-right tabular-nums">{row.quantity}</td>}
              <td className="py-1.5 pl-2 text-right tabular-nums">{row.total}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Items ranked within their category (a beer would otherwise crowd out every dish), with picture,
 * quantity sold and revenue; shared with the full list page.
 */
export function ItemList({ categories, main, decimals }: { categories: CategoryItems[]; main: Language; decimals: number }) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(main)
  if (categories.length === 0) return <p className="text-muted text-sm">{t('stats.nothingSold')}</p>
  return (
    <div className="flex flex-col gap-4">
      {categories.map((category) => (
        <section key={category.category_id} aria-label={name(category.name)} className="flex flex-col gap-1">
          <h3 className="text-muted text-sm font-medium">{name(category.name)}</h3>
          <ol className="divide-border divide-y">
            {category.items.map((item, index) => (
              <li key={item.item_id ?? `gone-${index}`} className="flex items-center gap-3 py-2">
                <span className="text-muted w-5 shrink-0 text-right text-sm tabular-nums">{index + 1}</span>
                {item.image_url ? (
                  <img src={item.image_url} alt="" className="size-12 shrink-0 rounded-lg object-cover" />
                ) : (
                  <span aria-hidden className="bg-accent-soft size-12 shrink-0 rounded-lg" />
                )}
                <div className="min-w-0 grow">
                  <p className="truncate font-medium">{name(item.name)}</p>
                  <p className="text-muted text-sm">
                    {t('stats.sold', { quantity: formatQuantity(item.quantity, language) })} · {t('stats.inOrders', { count: item.order_count })}
                    {item.item_id === null && <> · {t('stats.deletedItem')}</>}
                  </p>
                </div>
                <span className="shrink-0 font-medium tabular-nums">{formatPrice(item.total, decimals, language)}</span>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  )
}
